<template>
  <div>
    <div class="toolbar">
      <span class="small text-muted">
        <template v-if="showClosed">รายการที่ปิดไว้โดยไม่จับคู่ พร้อมเหตุผล เปิดกลับมาตรวจใหม่ได้</template>
        <template v-else>จับคู่บรรทัดในเอกสารกับรายการค้างรับ ระบบแนะนำรายการที่ใกล้เคียงให้ถ้ามี</template>
      </span>
      <div class="toolbar-right">
        <strong v-if="!showClosed" class="small">{{ rows.length.toLocaleString("th-TH") }} รายการ · รวม {{ formatAmount(total) }} บาท</strong>
        <b-button size="sm" :variant="showClosed ? 'secondary' : 'outline-secondary'" :pressed="showClosed" @click="showClosed = !showClosed">
          <i class="fas fa-archive"></i> รายการที่ปิดแล้ว ({{ closedLines.length.toLocaleString("th-TH") }})
        </b-button>
      </div>
    </div>

    <b-table
      v-if="showClosed"
      hover
      small
      head-variant="light"
      :items="closedRows"
      :fields="closedFields"
      responsive
      show-empty
      empty-text="ยังไม่มีรายการที่ปิด"
    >
      <template #cell(serviceDate)="row">
        {{ formatDate(row.item.serviceDate) }}
      </template>
      <template #cell(amount)="row">
        {{ formatAmount(row.item.amount) }}
      </template>
      <template #cell(note)="row">
        <span style="white-space: normal">{{ row.item.note }}</span>
        <div class="small text-muted">ปิดเมื่อ {{ formatDateTime(row.item.closedAt) }}</div>
      </template>
      <template #cell(buttons)="row">
        <b-button v-if="isHeadCounterOrManager" variant="outline-primary" size="sm" :disabled="busyLineId === row.item.id" @click="reopen(row.item)">
          <i class="fas fa-undo"></i> เปิดกลับ
        </b-button>
      </template>
    </b-table>

    <b-table
      v-else
      hover
      small
      sort-icon-left
      head-variant="light"
      :items="rows"
      :fields="fields"
      responsive
      show-empty
      empty-text="ไม่มีรายการรอตรวจสอบในช่วงนี้"
    >
      <template #cell(statementRef)="row">
        {{ row.item.statementRef }}
        <div v-if="isHeadCounterOrManager">
          <b-button
            variant="link"
            size="sm"
            class="p-0 text-danger undo-doc"
            :disabled="undoingBatchId === row.item.batchId"
            @click="undoStatement(row.item)"
          >
            ยกเลิกทั้งเอกสาร
          </b-button>
        </div>
      </template>
      <template #cell(serviceDate)="row">
        {{ formatDate(row.item.serviceDate) }}
      </template>
      <template #cell(amount)="row">
        {{ formatAmount(row.item.amount) }}
      </template>
      <template #cell(reason)="row">
        <div class="recon-reason">
          <span class="recon-reason-main">{{ reasonText(row.item).main }}</span>
          <span class="text-muted">{{ reasonText(row.item).sub }}</span>
        </div>
      </template>
      <template #cell(buttons)="row">
        <div v-if="isHeadCounterOrManager" class="d-flex justify-content-end" style="gap: 6px">
          <b-button
            v-if="suggestionOf(row.item).claim"
            variant="primary"
            size="sm"
            :disabled="busyLineId === row.item.id"
            @click="confirmMatch(row.item, suggestionOf(row.item).claim)"
          >
            จับคู่กับ {{ formatDate(suggestionOf(row.item).claim.serviceDate) }}
          </b-button>
          <b-button variant="outline-primary" size="sm" @click="openPicker(row.item)">
            <i class="fas fa-link"></i> เลือกเอง
          </b-button>
          <b-button variant="outline-secondary" size="sm" title="ปิดรายการโดยไม่จับคู่" @click="openClose(row.item)">
            ปิดรายการ
          </b-button>
        </div>
      </template>
    </b-table>

    <b-modal ref="pickerModal" title="เลือกรายการค้างรับที่ต้องการจับคู่" size="xl" scrollable ok-only ok-title="ปิด" ok-variant="secondary">
      <template v-if="activeLine">
        <div class="ref-card">
          <div class="ref-card-head">
            <span class="ref-card-title"><i class="fas fa-file-alt mr-1"></i> บรรทัดในเอกสาร</span>
            <span class="text-muted small">{{ activeLine.statementRef }}</span>
          </div>
          <div class="ref-grid">
            <div>
              <div class="ref-lbl">วันที่ใช้สิทธิ</div>
              <div class="ref-val">{{ formatDate(activeLine.serviceDate) || "-" }}</div>
            </div>
            <div>
              <div class="ref-lbl">เลขบัตร ปชช.</div>
              <div class="ref-val text-monospace">{{ activeLine.cid || "-" }}</div>
              <div v-if="activeLine.cid && !cidValid(activeLine.cid)" class="ref-warn">
                <i class="fas fa-exclamation-triangle"></i> เลขบัตรไม่ถูกต้อง อาจอ่านผิด
              </div>
            </div>
            <div>
              <div class="ref-lbl">ชื่อ-สกุล (ตามเอกสาร)</div>
              <div class="ref-val">{{ activeLine.patientName || "-" }}</div>
            </div>
            <div class="text-right">
              <div class="ref-lbl">จำนวนเงิน</div>
              <div class="ref-val">{{ formatAmount(activeLine.amount) }}</div>
            </div>
          </div>
          <div class="ref-reason">{{ reasonText(activeLine).main }} · {{ reasonText(activeLine).sub }}</div>
        </div>

        <div class="picker-toolbar">
          <span class="small">
            <template v-if="suggestedCount">
              <span class="badge-suggest">แนะนำ {{ suggestedCount }}</span>
              รายการที่ใกล้เคียงกับบรรทัดนี้อยู่ด้านบน
            </template>
            <template v-else>ไม่พบรายการที่ใกล้เคียง ลองค้นหาจากชื่อหรือเลขที่ใบเสร็จ</template>
          </span>
          <b-form-input
            v-model.trim="pickerKeyword"
            type="search"
            size="sm"
            placeholder="ค้นหาเลขบัตร ปชช. / ชื่อ / เลขที่ใบเสร็จ"
            class="picker-search"
          />
        </div>

        <b-table
          hover
          small
          head-variant="light"
          :items="pickerRows"
          :fields="pickerFields"
          :tbody-tr-class="(r) => (r && r.suggested ? 'row-suggested' : '')"
          responsive
          show-empty
          empty-text="ไม่มีรายการค้างรับให้จับคู่"
        >
          <template #cell(similarity)="row">
            <span v-if="row.item.suggested" class="badge-suggest mr-1">แนะนำ</span>
            <span v-for="sig in row.item.signals" :key="sig.field" class="sig" :class="`sig-${sig.level}`">
              {{ SIGNAL_LABEL[sig.field] }} {{ sig.text }}
            </span>
            <span v-if="!row.item.signals.length" class="text-muted small">ไม่ตรงกัน</span>
          </template>
          <template #cell(cid)="row">
            <span class="text-monospace">
              <span v-for="(part, i) in cidParts(row.item.claim.cid)" :key="i" :class="{ 'cid-diff': part.diff }">{{ part.ch }}</span>
            </span>
          </template>
          <template #cell(patientName)="row">
            {{ row.item.claim.patientName }}
          </template>
          <template #cell(serviceDate)="row">
            {{ formatDate(row.item.claim.serviceDate) }}
          </template>
          <template #cell(receiptNo)="row">
            {{ row.item.claim.receiptNo || "-" }}
            <div v-if="payer === 'NHSO' && row.item.claim.doctorName" class="small text-muted">{{ row.item.claim.doctorName }}</div>
          </template>
          <template #cell(amount)="row">
            {{ formatAmount(row.item.claim.amount) }}
          </template>
          <template #cell(buttons)="row">
            <b-button
              :variant="row.item.suggested ? 'primary' : 'outline-primary'"
              size="sm"
              :disabled="busyLineId === activeLine.id"
              @click="confirmMatch(activeLine, row.item.claim)"
            >
              จับคู่
            </b-button>
          </template>
        </b-table>
        <div v-if="pickerHidden" class="small text-muted">
          แสดง {{ PICKER_LIMIT }} รายการที่ใกล้เคียงที่สุด จาก {{ pickerMatches.length.toLocaleString("th-TH") }} รายการ ค้นหาเพื่อดูรายการอื่น
        </div>
      </template>
    </b-modal>

    <b-modal ref="closeModal" title="ปิดรายการโดยไม่จับคู่" ok-title="ปิดรายการ" ok-variant="danger" cancel-title="ยกเลิก" :ok-disabled="!closeNote.trim() || !!busyLineId" @ok="confirmClose">
      <template v-if="closingLine">
        <p class="small mb-2">
          {{ closingLine.statementRef }} · {{ formatDate(closingLine.serviceDate) }} · {{ closingLine.cid }} · {{ closingLine.patientName }} ·
          {{ formatAmount(closingLine.amount) }} บาท
        </p>
        <p class="small text-muted">
          ใช้เมื่อบรรทัดนี้จะไม่มีรายการค้างรับให้จับคู่ รายการที่ปิดจะไม่นับในรอตรวจสอบ ดูย้อนหลังและเปิดกลับได้ที่ “รายการที่ปิดแล้ว”
        </p>
        <div class="d-flex flex-wrap mb-2" style="gap: 6px">
          <button v-for="r in CLOSE_REASONS" :key="r" type="button" class="chip" :class="{ 'chip-active': closeNote === r }" @click="closeNote = r">{{ r }}</button>
        </div>
        <label for="close-note" class="small font-weight-bold">เหตุผล</label>
        <b-form-textarea id="close-note" v-model="closeNote" rows="2" maxlength="500" placeholder="เลือกด้านบน หรือพิมพ์เหตุผล" />
      </template>
    </b-modal>

    <Dialog ref="Dialog" />
    <Loading v-if="isLoading" />
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import Dialog from "@/components/modal/Dialog.vue";
import Loading from "@/components/Loading";
import {
  apiErrorMessage, formatThaiShortDate, isValidThaiCid, rankClaimsForLine, reviewSuggestion, undoStatementConfirm,
} from "@/utils/reconciliation";

const PICKER_LIMIT = 50;
// quick reasons for closing a line that will never match; staff can type their own
const CLOSE_REASONS = ["คนไข้ของสาขาอื่น", "ผู้จ่ายโอนซ้ำ", "visit นี้จับคู่กับเอกสารอื่นแล้ว", "ไม่พบข้อมูลคนไข้ในระบบ"];
const SIGNAL_LABEL = { cid: "เลขบัตร", name: "ชื่อ", date: "วันที่", amount: "ยอด" };

export default {
  name: "NeedsReviewTab",
  components: { Dialog, Loading },
  props: {
    payer: { type: String, required: true },
    // from PayerPanel: { dateFrom, dateTo, dateBasis, keyword, nonce } — filters on the line's date
    filter: { type: Object, required: true },
  },
  data() {
    return {
      isLoading: false,
      busyLineId: null,
      undoingBatchId: null,
      activeLine: null,
      // รายการที่ปิดแล้ว: lines closed without a match, with their reason
      showClosed: false,
      closedLines: [],
      closingLine: null,
      closeNote: "",
      CLOSE_REASONS,
      closedFields: [
        { key: "statementRef", label: "เอกสาร", sortable: true },
        { key: "serviceDate", label: "วันที่ใช้สิทธิ", sortable: true },
        { key: "cid", label: "เลขบัตร ปชช." },
        { key: "patientName", label: "ชื่อ-สกุล (ตามเอกสาร)" },
        { key: "amount", label: "จำนวนเงิน", tdClass: "text-right" },
        { key: "note", label: "เหตุผลที่ปิด" },
        { key: "buttons", label: "", class: "text-right" },
      ],
      pickerKeyword: "",
      // every outstanding claim of this payer (no date filter): the source for suggestions and the
      // picker, kept here rather than in the store so the ค้างรับ list stays as the user left it
      claims: [],
      fields: [
        { key: "statementRef", label: "เอกสาร", sortable: true },
        { key: "serviceDate", label: "วันที่ใช้สิทธิ", sortable: true },
        { key: "cid", label: "เลขบัตร ปชช.", sortable: true },
        { key: "patientName", label: "ชื่อ-สกุล (ตามเอกสาร)", sortable: true },
        { key: "amount", label: "จำนวนเงิน", sortable: true, tdClass: "text-right" },
        { key: "reason", label: "สาเหตุ" },
        { key: "buttons", label: "", class: "text-right" },
      ],
      PICKER_LIMIT,
      SIGNAL_LABEL,
      pickerFields: [
        { key: "similarity", label: "ความใกล้เคียง" },
        { key: "cid", label: "เลขบัตร ปชช." },
        { key: "patientName", label: "ชื่อ-สกุล" },
        { key: "serviceDate", label: "วันที่ใช้สิทธิ" },
        { key: "receiptNo", label: "เลขที่ใบเสร็จ" },
        { key: "amount", label: "จำนวนเงิน", class: "text-right" },
        { key: "buttons", label: "", class: "text-right" },
      ],
    };
  },
  computed: {
    ...mapGetters("moduleReconciliation", ["getNeedsReview"]),
    ...mapGetters({ isAccessMenu: "moduleUser/getIsAccessMenu" }),
    // reconciliation.manage (manual match) is MANAGER/HEADCOUNTER only on the
    // backend; COUNTER only has reconciliation.view. Gate the mutate control
    // here so a COUNTER sees a read-only tab instead of a button that 403s.
    isHeadCounterOrManager() {
      return this.isAccessMenu(["MANAGER", "HEADCOUNTER"]);
    },
    clinicUrl() {
      return this.$route.params.clinicUrl;
    },
    branchUrl() {
      return this.$route.params.branchUrl;
    },
    rows() {
      const k = (this.filter.keyword || "").toLowerCase();
      return this.getNeedsReview.filter((l) => !k || `${l.cid || ""} ${l.patientName || ""} ${l.statementRef || ""}`.toLowerCase().includes(k));
    },
    closedRows() {
      const k = (this.filter.keyword || "").toLowerCase();
      return this.closedLines.filter((l) => !k || `${l.cid || ""} ${l.patientName || ""} ${l.statementRef || ""}`.toLowerCase().includes(k));
    },
    total() {
      return this.rows.reduce((sum, l) => sum + Number(l.amount || 0), 0);
    },
    // every outstanding claim scored against the line being matched, closest first
    rankedClaims() {
      return this.activeLine ? rankClaimsForLine(this.activeLine, this.claims, { payer: this.payer }) : [];
    },
    suggestedCount() {
      return this.rankedClaims.filter((r) => r.suggested).length;
    },
    pickerMatches() {
      const k = this.pickerKeyword.trim().toLowerCase();
      if (!k) return this.rankedClaims;
      return this.rankedClaims.filter(({ claim: c }) => `${c.cid || ""} ${c.patientName || ""} ${c.receiptNo || ""}`.toLowerCase().includes(k));
    },
    pickerRows() {
      return this.pickerMatches.slice(0, PICKER_LIMIT);
    },
    pickerHidden() {
      return this.pickerMatches.length > PICKER_LIMIT;
    },
  },
  watch: {
    payer() {
      this.load();
    },
    "filter.nonce"() {
      this.load();
    },
  },
  mounted() {
    this.load();
  },
  methods: {
    ...mapActions("moduleReconciliation", [
      "fetchUnmatched", "fetchOutstanding", "fetchClosedLines", "manualMatch", "updateLineStatus", "undoBatch",
    ]),
    async load() {
      this.isLoading = true;
      try {
        const [, out, closed] = await Promise.all([
          this.fetchUnmatched({
            clinicUrl: this.clinicUrl,
            branchUrl: this.branchUrl,
            payer: this.payer,
            dateFrom: this.filter.dateFrom,
            dateTo: this.filter.dateTo,
          }),
          // claims served before go-live too: a late statement can pay those
          this.fetchOutstanding({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, payer: this.payer, includeBeforeStart: true, keepList: true }),
          this.fetchClosedLines({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, payer: this.payer }),
        ]);
        this.claims = Array.isArray(out.data) ? out.data : out.data.claims || [];
        this.closedLines = closed;
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "โหลดรายการรอตรวจสอบไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.isLoading = false;
      }
    },
    suggestionOf(line) {
      return reviewSuggestion(line, this.claims);
    },
    reasonText(line) {
      const s = this.suggestionOf(line);
      // Thai runs words together; an English word needs spaces around it
      const subject = this.payer === "NHSO" ? " visit " : "รายการ";
      if (s.kind === "date-mismatch") {
        return {
          main: `วันที่ไม่ตรงกับ${subject}ค้างรับ`.replace(/\s+/g, " "),
          sub: `ผู้ป่วยคนนี้มี${subject}ค้างรับวันที่ ${this.formatDate(s.claim.serviceDate)}${s.claim.receiptNo ? ` (${s.claim.receiptNo})` : ""}`,
        };
      }
      if (s.kind === "cid-misread") {
        return { main: "เลขบัตรไม่ถูกต้อง", sub: "อาจอ่านผิด กดเลือกเองเพื่อดูรายการที่ชื่อหรือวันที่ใกล้เคียง" };
      }
      return {
        main: `ไม่พบ${subject}ของเลขบัตรนี้`.replace(/\s+/g, " "),
        sub: this.payer === "NHSO" ? "อาจยังไม่ได้ออกใบเสร็จ หรือนัดนี้ไม่ได้ส่งข้อมูล KTB" : "อาจยังไม่ได้ออกใบเสร็จ หรือออกด้วยวิธีชำระอื่น",
      };
    },
    // A statement that matched nothing never appears under รับแล้ว, so it is undone from here too.
    async undoStatement(line) {
      const reviewCount = this.getNeedsReview.filter((l) => l.batchId === line.batchId).length;
      const confirmed = await this.$bvModal.msgBoxConfirm(undoStatementConfirm(line.statementRef, reviewCount), {
        title: "ยืนยันการยกเลิกเอกสาร", okVariant: "danger", okTitle: "ยกเลิกทั้งเอกสาร", cancelTitle: "ปิด",
      });
      if (!confirmed) return;
      this.undoingBatchId = line.batchId;
      try {
        await this.undoBatch({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, batchId: line.batchId });
        this.$refs.Dialog.showToast("success", `ยกเลิกเอกสาร ${line.statementRef} แล้ว นำเข้าใหม่ได้`);
        this.$emit("matched");
        await this.load();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "ยกเลิกเอกสารไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.undoingBatchId = null;
      }
    },
    openClose(line) {
      this.closingLine = line;
      this.closeNote = "";
      this.$refs.closeModal.show();
    },
    async confirmClose(bvModalEvent) {
      bvModalEvent.preventDefault();
      const line = this.closingLine;
      if (!line || !this.closeNote.trim()) return;
      this.busyLineId = line.id;
      try {
        await this.updateLineStatus({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, lineId: line.id, action: "ignore", note: this.closeNote.trim() });
        this.$refs.closeModal.hide();
        this.$refs.Dialog.showToast("success", "ปิดรายการแล้ว ดูได้ที่รายการที่ปิดแล้ว");
        this.$emit("matched");
        await this.load();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "ปิดรายการไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.busyLineId = null;
      }
    },
    async reopen(line) {
      this.busyLineId = line.id;
      try {
        await this.updateLineStatus({ clinicUrl: this.clinicUrl, branchUrl: this.branchUrl, lineId: line.id, action: "reopen" });
        this.$refs.Dialog.showToast("success", "เปิดรายการกลับมาที่รอตรวจสอบแล้ว");
        this.$emit("matched");
        await this.load();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "เปิดรายการไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.busyLineId = null;
      }
    },
    formatDateTime(date) {
      return formatThaiShortDate(date, { withTime: true });
    },
    openPicker(line) {
      this.activeLine = line;
      this.pickerKeyword = "";
      this.$refs.pickerModal.show();
    },
    async confirmMatch(line, claim) {
      if (!line || !claim) return;
      this.busyLineId = line.id;
      try {
        await this.manualMatch({
          clinicUrl: this.clinicUrl,
          branchUrl: this.branchUrl,
          lineId: line.id,
          claimId: claim.claimId,
        });
        this.$refs.Dialog.showToast("success", "จับคู่รายการสำเร็จ");
        this.$refs.pickerModal.hide();
        this.activeLine = null;
        this.$emit("matched");
        await this.load();
      } catch (err) {
        this.$refs.Dialog.showToast("danger", apiErrorMessage(err, "จับคู่รายการไม่สำเร็จ กรุณาลองใหม่"));
      } finally {
        this.busyLineId = null;
      }
    },
    cidValid(cid) {
      return isValidThaiCid(cid);
    },
    // the claim's CID digit by digit, flagging where it differs from the line's (OCR slips stand out)
    cidParts(cid) {
      const ref = String(this.activeLine?.cid || "");
      return [...String(cid || "")].map((ch, i) => ({ ch, diff: ref.length === 13 && ref[i] !== ch }));
    },
    formatDate(date) {
      return formatThaiShortDate(date);
    },
    formatAmount(amount) {
      return Number(amount || 0).toLocaleString("th-TH", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    },
  },
};
</script>

<style scoped>
.ref-card {
  border: 1px solid #c9d8f6;
  background: #f7faff;
  border-radius: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
}
.ref-card-head {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.ref-card-title {
  font-weight: 600;
  color: #2a6df4;
}
.ref-grid {
  display: grid;
  grid-template-columns: 1fr 1.3fr 1.6fr 1fr;
  gap: 12px;
}
.ref-lbl {
  font-size: 11.5px;
  color: #5f6b7a;
}
.ref-val {
  font-weight: 600;
  color: #1f2933;
}
.ref-warn {
  font-size: 11.5px;
  color: #b45309;
}
.ref-reason {
  margin-top: 8px;
  font-size: 12.5px;
  color: #8a5300;
}
.picker-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}
.picker-search {
  max-width: 320px;
}
.badge-suggest {
  display: inline-block;
  background: #2a6df4;
  color: #fff;
  border-radius: 10px;
  padding: 1px 8px;
  font-size: 11.5px;
  font-weight: 600;
}
.sig {
  display: inline-block;
  border-radius: 10px;
  padding: 1px 8px;
  margin: 1px 4px 1px 0;
  font-size: 11.5px;
  white-space: nowrap;
}
.sig-exact {
  background: #e6f4ea;
  color: #1e7b34;
}
.sig-near {
  background: #fff4e0;
  color: #8a5300;
}
.cid-diff {
  color: #c0392b;
  font-weight: 700;
  text-decoration: underline;
}
::v-deep .row-suggested {
  background: #f7faff;
}
@media (max-width: 991px) {
  .ref-grid {
    grid-template-columns: 1fr 1fr;
  }
}
.undo-doc {
  font-size: 12px;
}
.recon-reason {
  display: flex;
  flex-direction: column;
  white-space: normal;
  min-width: 220px;
}
.recon-reason-main {
  font-weight: 600;
  color: #8a5300;
}
</style>
